import { useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import {
  SEARCH_DEBOUNCE_MS,
  SEARCH_MIN_QUERY_LENGTH,
} from "./folderSearchConstants";

interface FolderSearchInputProps {
  onChange: (q: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
}

export default function FolderSearchInput({
  onChange,
  placeholder = "Search sites and folders...",
  autoFocus = false,
}: FolderSearchInputProps) {
  const [value, setValue] = useState("");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Cancel in-flight debounce on unmount.
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  const scheduleOnChange = (next: string) => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const trimmed = next.trim();
    // Empty or sub-minimum: clear the search immediately so stale results
    // do not linger via keepPreviousData.
    if (trimmed.length < SEARCH_MIN_QUERY_LENGTH) {
      onChange("");
      return;
    }
    timerRef.current = setTimeout(() => {
      onChange(trimmed);
      timerRef.current = null;
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setValue(next);
    scheduleOnChange(next);
  };

  const handleClear = () => {
    setValue("");
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    onChange("");
    inputRef.current?.focus();
  };

  return (
    <div className="relative px-5 py-3 border-b border-border shrink-0">
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
          <Search className="w-4 h-4" />
        </span>
        <input
          ref={inputRef}
          type="text"
          role="searchbox"
          aria-label="Search SharePoint sites and folders"
          autoFocus={autoFocus}
          value={value}
          placeholder={placeholder}
          onChange={handleChange}
          className="w-full pl-9 pr-9 py-2 text-[13px] bg-muted/40 border border-border rounded-lg text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
          data-testid="folder-search-input"
        />
        {value.length > 0 && (
          <button
            type="button"
            onClick={handleClear}
            aria-label="Clear search"
            data-testid="folder-search-clear"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
